/**
 * Helpers that preserve the server-chosen download filename across blob fetches.
 *
 * Both fetch wrappers (`appFetch`, `volunteerFetch`) hand PDF/CSV responses to
 * `namedBlobFromResponse`, which returns a File carrying the name advertised in
 * the response's Content-Disposition header. The backend therefore stays the
 * single source of truth for export filenames — download callers no longer
 * hardcode a name client-side.
 */

// Pull the server-chosen download name out of a Content-Disposition header.
// Handles both `filename*=UTF-8''...` (RFC 5987) and the plain quoted
// `filename="..."` form.
export function filenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // Malformed encoding: fall back to the plain filename form below.
    }
  }

  const quotedMatch = header.match(/filename="?([^"]+)"?/i);
  return quotedMatch?.[1]?.trim() || null;
}

// Materialize a response body as a File carrying the server filename when one is
// advertised. File extends Blob, so every existing `instanceof Blob` caller
// keeps working while callers that omit an explicit name download under the
// backend's normalized filename.
export async function namedBlobFromResponse(response: Response): Promise<Blob> {
  const blob = await response.blob();
  const name = filenameFromContentDisposition(
    response.headers.get("content-disposition"),
  );

  return name ? new File([blob], name, { type: blob.type }) : blob;
}
